import React, { useState, useEffect } from 'react';
import FamilyPlazaSubHeading from '../../../components/family-plaza/FamilyPlazaSubHeading';
import FamilyPlazaTopSection from '../../../components/family-plaza/FamilyPlazaTopSection';
import PrintPreviewModal from '../../../components/family-plaza/PrintPreviewModal';
import { AncestralTabService } from '../../../services';

const AncestralTabletPC = () => {
  const [selectedAncestralSubject, setSelectedAncestralSubject] = useState('great-great-grandparents');
  const [selectedMaleTitle, setSelectedMaleTitle] = useState('student');
  const [selectedFemaleInput, setSelectedFemaleInput] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [orientation, setOrientation] = useState('landscape'); // 기본값: landscape

  const handleAncestralSubjectChange = (subjectId) => {
    setSelectedAncestralSubject(subjectId);
  };

  const handleMaleTitleChange = (titleId) => {
    setSelectedMaleTitle(titleId);
  };

  const handleFemaleInputChange = (inputId) => {
    setSelectedFemaleInput(inputId);
  };

  const handleTextInputChange = (value) => {
    setInputValue(value);
  };

  const handleLastNameCheck = async () => {
    const lastname = inputValue;
    try {
      const response = await AncestralTabService.printCheck({
        lastname: lastname
      });
      console.log('PC Ancestral Tablet Lastname Check Response:', response);
      alert(response.message);
      //setInputValue(response.lastnameHanja);
    } catch (error) {
      console.log('API error:', error);
      alert(error.message);
    }
  };


  const handlePrintAncestralTablet = async () => {
    // Get form values using JSP logic
    const goin = getSelectedRelationship();
    const position = getSelectedPosition();
    const lastname = getSelectedLastname();
    const sex = determineGender(goin, position, lastname);

    // Debug logging
    console.log('Debug values:', { goin, position, lastname, sex });

    // Create output data
    const outputData = {
      goin: goin,
      position: position,
      lastname: lastname,
      sex: sex,
      timestamp: new Date().toISOString()
    };

    console.log('PC Ancestral Tablet Output Data:', outputData);

    try {
      const response = await AncestralTabService.printProcess({
        goin: goin,
        position: position,
        lastname: lastname
      });
      console.log('PC Ancestral Tablet Response:', response);

      if (response) {
        setShowPrintPreview(true);
        // Prepare print data outside of setTimeout to reduce handler execution time
        const printData = {
          goin: response.goin,
          position: response.positionHanja,
          lastname: response.lastnameHanja
        };
        
        setTimeout(() => {
          setShowPrintPreview(false);
          // Use requestAnimationFrame to defer heavy DOM operations
          requestAnimationFrame(() => {
            handlePrint(printData.goin, printData.position, printData.lastname);
          });
        }, 50);
      }
    } catch (error) {
      console.error('Error processing print request:', error);
      alert('지방 출력 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  // Helper functions to match JSP logic
  const getSelectedRelationship = () => {
    // Use React state instead of querying DOM
    if (!selectedAncestralSubject) return '0|1'; // Default to 고조부모

    // Map React values to JSP values
    const relationshipMap = {
      'great-great-grandparents': '0|1',
      'great-great-grandfather': '0',
      'great-great-grandmother': '1',
      'great-grandparents': '2|3',
      'great-grandfather': '2',
      'great-grandmother': '3',
      'grandparents': '4|5',
      'grandfather': '4',
      'grandmother': '5',
      'granduncle-aunt': '6|7',
      'granduncle': '6',
      'granduncle-wife': '7',
      'parents': '8|9',
      'father': '8',
      'mother': '9',
      'wife': '10',
      'husband': '11',
      'younger-brother-wife': '12',
      'younger-sibling': '13',
      'elder-brother-wife': '14',
      'elder-brother': '15'
    };

    return relationshipMap[selectedAncestralSubject] || '0|1';
  };

  const getSelectedPosition = () => {
    // Use React state instead of querying DOM
    if (!selectedMaleTitle) return '학생'; // Default to 학생

    // Map React values to JSP values
    const positionMap = {
      'student': '학생',
      'scholar': '학자',
      'legislator': '의원',
      'chairman': '회장',
      'judge': '법관',
      'military': '군관',
      'executive': '임원',
      'professional': '처사'
    };

    return positionMap[selectedMaleTitle] || '학생';
  };

  const getSelectedLastname = () => {
    // Use React state instead of querying DOM
    return inputValue ? inputValue.trim() : ''; // Default to 김
  };

  const determineGender = (goin, position, lastname) => {
    if (!goin) return 'MF'; // Default to couple

    // Check if it's a couple (contains |)
    if (goin.includes('|')) {
      return 'MF'; // Male and Female
    }

    // Single person - determine gender based on JSP logic
    const maleRelationships = ['0', '2', '4', '6', '8', '11', '15'];
    const femaleRelationships = ['1', '3', '5', '7', '9', '10', '12', '14'];

    if (maleRelationships.includes(goin)) {
      return 'M';
    } else if (femaleRelationships.includes(goin)) {
      return 'F';
    } else if (goin === '13') { // 동생 - ambiguous
      return position ? 'M' : 'F';
    }

    return 'MF'; // Default to couple
  };

  const handlePrint = (goin, position, lastname) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=920,height=640,scrollbars=yes,resizable=yes');

    if (printWindow) {
      // Pre-generate tablet content to avoid doing it in the setTimeout
      const tabletContent = generateTablets(goin, position, lastname);
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
          <title>지방출력 | 파라다이스 추모원</title>
          <style>
            @page {
              size: A4 ${orientation};
              margin: 0.4in 0.15in 0.4in 0.25in;
            }
            @media print {
              body { 
                margin: 0;
                padding: 0;
                width: 100%;
                height: 100%;
                overflow: hidden;
              }
              .no-print { display: none !important; }
              * {
                page-break-inside: avoid;
                page-break-after: avoid;
              }
              div[style*="float:left"] {
                page-break-inside: avoid;
                page-break-after: avoid;
              }
            }
          </style>
        </head>
        <body bgcolor="#FFFFFF" text="#000000" leftmargin="0" topmargin="0" marginwidth="0" marginheight="0" align="center">
          <div style="width:100%; height:100%; text-align:center; page-break-inside:avoid; overflow:hidden; padding-left:5px;">
            ${tabletContent}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
    } else {
      alert('팝업이 차단되었습니다. 팝업을 허용해주세요.');
    }
  };

  const generateTablets = (goin, position, lastname) => {
    // Pre-generate tablet content once to avoid repeated calls
    const tabletContent = generateTabletContent(goin, position, lastname);
    
    // Use array join for better performance than string concatenation
    const tablets = [];
    const tabletTemplate = `
        <div style="float:left; margin:0 1px 0 0; position:relative; font-family:'바탕체';width:148px; height:580px;">
      <img src="https://www.prds.kr/images/popup_jibang_bg.png" style="position:absolute; top:0; left:0; width:148px; height:580px;" />
          <div style="position:absolute; top:0; left:0; font-size:42px;font-family:'바탕체'; width:148px; height:580px;">
            ${tabletContent}
          </div>
        </div>
      `;

    // Generate 5 tablets
    for (let i = 0; i < 5; i++) {
      tablets.push(tabletTemplate);
    }

    return tablets.join('');
  };

  const generateTabletContent = (goin, position, lastname) => {
    // Exact logic for generating tablet content with conditional line breaks
    const hasLastName = lastname && lastname.trim() !== '';
    const femaleNameBreaks = hasLastName ? '' : '<br><br><br><br>';

    if (goin === '0|1') { // 고조부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:55px;">顯高祖考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:45px;">顯高祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:26px;font-family:'바탕체'; width:30px; line-height:46px;">神位</div>
        <div style="position:absolute; top:474px; left:82px;font-family:'바탕체'; width:30px; line-height:46px;">神位</div>
      `;
    } else if (goin === '0') { // 고조부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">顯高祖考${position}府君</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '1') { // 고조모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:46.0px;">顯高祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:52px;font-family:'바탕체'; width:30px; line-height:46px;">神位</div>
      `;
    } else if (goin === '2|3') { // 증조부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:50px;">顯曾祖考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:45px;">顯曾祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:474px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '2') { // 증조부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">顯曾祖考${position}府君</div>
        <div style="position:absolute; top:460px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '3') { // 증조모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:45px;">顯曾祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:45px;">神位</div>
      `;
    } else if (goin === '4|5') { // 조부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:60px;">顯祖考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:50px;">顯祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:474px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:474px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '4') { // 조부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:60px;">顯祖考${position}府君</div>
        <div style="position:absolute; top:450px; left:52px;font-family:'바탕체'; width:30px; line-height:60px;">神位</div>
      `;
    } else if (goin === '5') { // 조모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">顯祖妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:464px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '6|7') { // 백부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:60px;">顯伯父${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:50px;">顯伯母孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:469px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '6') { // 백부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:60px;">顯伯父${position}府君</div>
        <div style="position:absolute; top:450px; left:52px;font-family:'바탕체'; width:30px; line-height:60px;">神位</div>
      `;
    } else if (goin === '7') { // 백모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">顯伯母孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '8|9') { // 부모
      return `
        <div style="position:absolute; top:25px; left:26px;font-family:'바탕체'; width:39px;  line-height:65px;">顯考${position}府君</div>
        <div style="position:absolute; top:29px; left:82px;font-family:'바탕체'; width:39px;  line-height:55px;">顯妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:26px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
        <div style="position:absolute; top:469px; left:82px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '8') { // 부
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">顯考${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '9') { // 모
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">顯妣孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:460px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '11') { // 남편
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">顯辟${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '10') { // 처
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:55px;">亡室孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:450px; left:52px;font-family:'바탕체'; width:30px; line-height:55px;">神位</div>
      `;
    } else if (goin === '15') { // 형
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">顯兄${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '14') { // 형수
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">顯兄嫂孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:469px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    } else if (goin === '13') { // 동생
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:65px;">亡弟${position}府君</div>
        <div style="position:absolute; top:440px; left:52px;font-family:'바탕체'; width:30px; line-height:65px;">神位</div>
      `;
    } else if (goin === '12') { // 제수
      return `
        <div style="position:absolute; top:19px; left:52px;font-family:'바탕체'; width:39px;  line-height:50px;">亡弟嫂孺人${femaleNameBreaks}${lastname}氏</div>
        <div style="position:absolute; top:464px; left:52px;font-family:'바탕체'; width:30px; line-height:50px;">神位</div>
      `;
    }

    return '';
  };


  return (
    <div className="announcement-list-page cyber-info-page-pc">

      <FamilyPlazaTopSection />

      <main className="main-content">

        <FamilyPlazaSubHeading heading="지방출력" />

        {/* Main Content */}
        <div className="ancestral-tablet-main-content">
          <div className="ancestral-tablet-page-content">
            <div className="ancestral-tablet-container">

              {/* Content Area - Ancestral Tablet */}
              <div className="ancestral-tablet-content-area">
                <div className="ancestral-tablet-instruction">
                  <p>즉시 사용 가능한 지방을 출력할 수 있습니다. <strong>제사에 필요한 지방! 한 번의 클릭으로 출력하세요.</strong></p>
                </div>

                {/* 제사 대상 Section */}
                <div className="ancestral-tablet-section">
                  <h3 className="ancestral-tablet-section-title">제사 대상</h3>
                  <div className="ancestral-tablet-options-grid">
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="great-great-grandparents"
                        checked={selectedAncestralSubject === 'great-great-grandparents'}
                        onChange={() => handleAncestralSubjectChange('great-great-grandparents')}
                      />
                      <label htmlFor="great-great-grandparents">고조부모</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="great-grandparents"
                        checked={selectedAncestralSubject === 'great-grandparents'}
                        onChange={() => handleAncestralSubjectChange('great-grandparents')}
                      />
                      <label htmlFor="great-grandparents">증조부모</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="grandparents"
                        checked={selectedAncestralSubject === 'grandparents'}
                        onChange={() => handleAncestralSubjectChange('grandparents')}
                      />
                      <label htmlFor="grandparents">조부모</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="granduncle-aunt"
                        checked={selectedAncestralSubject === 'granduncle-aunt'}
                        onChange={() => handleAncestralSubjectChange('granduncle-aunt')}
                      />
                      <label htmlFor="granduncle-aunt">백부모</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="parents"
                        checked={selectedAncestralSubject === 'parents'}
                        onChange={() => handleAncestralSubjectChange('parents')}
                      />
                      <label htmlFor="parents">부모</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="husband"
                        checked={selectedAncestralSubject === 'husband'}
                        onChange={() => handleAncestralSubjectChange('husband')}
                      />
                      <label htmlFor="husband">남편</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="elder-brother-wife"
                        checked={selectedAncestralSubject === 'elder-brother-wife'}
                        onChange={() => handleAncestralSubjectChange('elder-brother-wife')}
                      />
                      <label htmlFor="elder-brother-wife">형수</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="great-great-grandfather"
                        checked={selectedAncestralSubject === 'great-great-grandfather'}
                        onChange={() => handleAncestralSubjectChange('great-great-grandfather')}
                      />
                      <label htmlFor="great-great-grandfather">고조부</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="great-grandfather"
                        checked={selectedAncestralSubject === 'great-grandfather'}
                        onChange={() => handleAncestralSubjectChange('great-grandfather')}
                      />
                      <label htmlFor="great-grandfather">증조부</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="grandfather"
                        checked={selectedAncestralSubject === 'grandfather'}
                        onChange={() => handleAncestralSubjectChange('grandfather')}
                      />
                      <label htmlFor="grandfather">조부</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="granduncle"
                        checked={selectedAncestralSubject === 'granduncle'}
                        onChange={() => handleAncestralSubjectChange('granduncle')}
                      />
                      <label htmlFor="granduncle">백부</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="father"
                        checked={selectedAncestralSubject === 'father'}
                        onChange={() => handleAncestralSubjectChange('father')}
                      />
                      <label htmlFor="father">부</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="wife"
                        checked={selectedAncestralSubject === 'wife'}
                        onChange={() => handleAncestralSubjectChange('wife')}
                      />
                      <label htmlFor="wife">처</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="younger-sibling"
                        checked={selectedAncestralSubject === 'younger-sibling'}
                        onChange={() => handleAncestralSubjectChange('younger-sibling')}
                      />
                      <label htmlFor="younger-sibling">동생</label>
                    </div>
                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="great-great-grandmother"
                        checked={selectedAncestralSubject === 'great-great-grandmother'}
                        onChange={() => handleAncestralSubjectChange('great-great-grandmother')}
                      />
                      <label htmlFor="great-great-grandmother">고조모</label>
                    </div>


                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="great-grandmother"
                        checked={selectedAncestralSubject === 'great-grandmother'}
                        onChange={() => handleAncestralSubjectChange('great-grandmother')}
                      />
                      <label htmlFor="great-grandmother">증조모</label>
                    </div>


                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="grandmother"
                        checked={selectedAncestralSubject === 'grandmother'}
                        onChange={() => handleAncestralSubjectChange('grandmother')}
                      />
                      <label htmlFor="grandmother">조모</label>
                    </div>


                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="granduncle-wife"
                        checked={selectedAncestralSubject === 'granduncle-wife'}
                        onChange={() => handleAncestralSubjectChange('granduncle-wife')}
                      />
                      <label htmlFor="granduncle-wife">백모</label>
                    </div>


                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="mother"
                        checked={selectedAncestralSubject === 'mother'}
                        onChange={() => handleAncestralSubjectChange('mother')}
                      />
                      <label htmlFor="mother">모</label>
                    </div>


                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="elder-brother"
                        checked={selectedAncestralSubject === 'elder-brother'}
                        onChange={() => handleAncestralSubjectChange('elder-brother')}
                      />
                      <label htmlFor="elder-brother">형</label>
                    </div>


                    <div className="ancestral-tablet-option">
                      <input
                        type="radio"
                        name="ancestral-subject"
                        id="younger-brother-wife"
                        checked={selectedAncestralSubject === 'younger-brother-wife'}
                        onChange={() => handleAncestralSubjectChange('younger-brother-wife')}
                      />
                      <label htmlFor="younger-brother-wife">제수</label>
                    </div>
                  </div>
                </div>

                {/* 제사 대상 성별 Section */}
                <div className="ancestral-tablet-section">
                  <h3 className="ancestral-tablet-section-title">제사 대상 성별</h3>

                  {/* Show current design for great-great-grandparents (first option) */}
                  {selectedAncestralSubject === 'great-great-grandparents' && (
                    <div className="ancestral-tablet-gender-section ancestral-tablet-gender-section-option1">
                      <div className="ancestral-tablet-gender-male">
                        <div className="ancestral-tablet-text-container">
                          <h4 className="ancestral-tablet-gender-title">남성</h4>
                          <p className="ancestral-tablet-gender-description">남자의 지방에는 제사 대상의 관직명이 표시됩니다.<br /> 아래에서 선택한 광직명이 선택하신 기본 양식에 적용되어 출력됩니다.</p>
                        </div>
                        <div className="ancestral-tablet-male-options">
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="student"
                              checked={selectedMaleTitle === 'student'}
                              onChange={() => handleMaleTitleChange('student')}
                            />
                            <label htmlFor="student">학생 - 일반적으로 가장 널리 쓰임</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="chairman"
                              checked={selectedMaleTitle === 'chairman'}
                              onChange={() => handleMaleTitleChange('chairman')}
                            />
                            <label htmlFor="chairman">회장 - 법인/단체의 장(00협회장,00회장 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="military-officer"
                              checked={selectedMaleTitle === 'military'}
                              onChange={() => handleMaleTitleChange('military')}
                            />
                            <label htmlFor="military-officer">군관 - 군인의 경우 사용(장군, 고위장교 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="scholar"
                              checked={selectedMaleTitle === 'professional'}
                              onChange={() => handleMaleTitleChange('professional')}
                            />
                            <label htmlFor="scholar">처사 - 전문직 종사자(회계사, 변리사등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="judge"
                              checked={selectedMaleTitle === 'judge'}
                              onChange={() => handleMaleTitleChange('judge')}
                            />
                            <label htmlFor="judge">법관 - 법조인의 경우 사용(판, 검사, 변호사 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="executive"
                              checked={selectedMaleTitle === 'executive'}
                              onChange={() => handleMaleTitleChange('executive')}
                            />
                            <label htmlFor="executive">임원 - 대기업 고위직의 경우(대기업 회장, 사장, 간부 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="legislator"
                              checked={selectedMaleTitle === 'legislator'}
                              onChange={() => handleMaleTitleChange('legislator')}
                            />
                            <label htmlFor="legislator">의원 - 정치인(국회의원, 자치단체의원)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="academic"
                              checked={selectedMaleTitle === 'scholar'}
                              onChange={() => handleMaleTitleChange('scholar')}
                            />
                            <label htmlFor="academic">학자 - 학계에 계셨던 분(교수, 박사, 학자 등)</label>
                          </div>
                        </div>
                      </div>

                      <div className="ancestral-tablet-gender-female">
                        <div className="ancestral-tablet-text-container">
                          <h4 className="ancestral-tablet-gender-title">여성</h4>
                          <p className="ancestral-tablet-gender-description">제사 대상이 여성일 경우 본관과 성씨가 입력됩니다.<br /> 본관 성씨를 빈칸에 한글로 적으시면 선택하신 기본양식에 적용되어 출력됩니다.</p>
                        </div>
                        <div className="ancestral-tablet-female-input">
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="female-input"
                              id="bon-gwan-input"
                              checked={selectedFemaleInput === 'bon-gwan-input'}
                              onChange={() => handleFemaleInputChange('bon-gwan-input')}
                            />
                            <label htmlFor="bon-gwan-input">본관 성씨 입력
                            </label>
                          </div>
                          <div className="ancestral-tablet-input-group">
                            <input
                              type="text"
                              className="ancestral-tablet-text-input"
                              placeholder="예) 김해김, 경주김"
                              value={inputValue}
                              onChange={(e) => handleTextInputChange(e.target.value)}
                            />
                            <button className="ancestral-tablet-confirm-btn" onClick={() => handleLastNameCheck()}>확인</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show new design for male subjects (like great-great-grandfather) */}
                  {['great-great-grandfather', 'great-grandfather', 'grandfather', 'granduncle', 'father', 'husband', 'elder-brother'].includes(selectedAncestralSubject) && (
                    <div className="ancestral-tablet-gender-section ancestral-tablet-gender-section-option2">
                      <div className="ancestral-tablet-gender-male">
                        <div className="ancestral-tablet-text-container">
                          <h4 className="ancestral-tablet-gender-title">남성</h4>
                          <p className="ancestral-tablet-gender-description">남자의 지방에는 제사 대상의 관직명이 표시됩니다. <br /> 아래에서 선택한 광직명이 선택하신 기본 양식에 적용되어 출력됩니다.</p>
                        </div>
                        <div className="ancestral-tablet-male-options">
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="student-new"
                              checked={selectedMaleTitle === 'student'}
                              onChange={() => handleMaleTitleChange('student')}
                            />
                            <label htmlFor="student-new">학생 - 일반적으로 가장 널리쓰임</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="scholar-new"
                              checked={selectedMaleTitle === 'professional'}
                              onChange={() => handleMaleTitleChange('professional')}
                            />
                            <label htmlFor="scholar-new">처사 - 전문직 종사자(회계사, 변리사등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="legislator-new"
                              checked={selectedMaleTitle === 'legislator'}
                              onChange={() => handleMaleTitleChange('legislator')}
                            />
                            <label htmlFor="legislator-new">의원 - 정치인(국회의원, 자치단체의원)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="chairman-new"
                              checked={selectedMaleTitle === 'chairman'}
                              onChange={() => handleMaleTitleChange('chairman')}
                            />
                            <label htmlFor="chairman-new">회장 - 법인/단체의 장(00협회장,00회장 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="judge-new"
                              checked={selectedMaleTitle === 'judge'}
                              onChange={() => handleMaleTitleChange('judge')}
                            />
                            <label htmlFor="judge-new">법관 - 법조인의 경우 사용(판, 검사, 변호사 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="academic-new"
                              checked={selectedMaleTitle === 'scholar'}
                              onChange={() => handleMaleTitleChange('scholar')}
                            />
                            <label htmlFor="academic-new">학자 - 학계에 계셨던 분(교수, 박사, 학자 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="military-officer-new"
                              checked={selectedMaleTitle === 'military'}
                              onChange={() => handleMaleTitleChange('military')}
                            />
                            <label htmlFor="military-officer-new">군관 - 군인의 경우 사용(장군, 고위장교 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="executive-new"
                              checked={selectedMaleTitle === 'executive'}
                              onChange={() => handleMaleTitleChange('executive')}
                            />
                            <label htmlFor="executive-new">임원 - 대기업 고위직의 경우(대기업 회장, 사장, 간부 등)</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show female input for female subjects */}
                  {['great-great-grandmother', 'great-grandmother', 'grandmother', 'granduncle-wife', 'mother', 'wife', 'elder-brother-wife', 'younger-brother-wife'].includes(selectedAncestralSubject) && (
                    <div className="ancestral-tablet-gender-section ancestral-tablet-gender-section-option2">
                      <div className="ancestral-tablet-gender-female">
                        <div className="ancestral-tablet-text-container">
                          <h4 className="ancestral-tablet-gender-title">여성</h4>
                          <p className="ancestral-tablet-gender-description">제사 대상이 여성일 경우 본관과 성씨가 입력됩니다. <br /> 본관 성씨를 빈칸에 한글로 적으시면 선택하신 기본양식에 적용되어 출력됩니다.</p>
                        </div>
                        <div className="ancestral-tablet-female-input">
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="female-input"
                              id="bon-gwan-input-new"
                              checked={selectedFemaleInput === 'bon-gwan-input-new'}
                              onChange={() => handleFemaleInputChange('bon-gwan-input-new')}
                            />
                            <label htmlFor="bon-gwan-input-new">본관 성씨 입력
                            </label>
                          </div>
                          <div className="ancestral-tablet-input-group">
                            <input
                              type="text"
                              className="ancestral-tablet-text-input"
                              placeholder="예) 김해김, 경주김"
                              value={inputValue}
                              onChange={(e) => handleTextInputChange(e.target.value)}
                            />
                            <button className="ancestral-tablet-confirm-btn" onClick={() => handleLastNameCheck()}>확인</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show both options for mixed subjects */}
                  {['great-grandparents', 'grandparents', 'granduncle-aunt', 'parents', 'younger-sibling'].includes(selectedAncestralSubject) && (
                    <div className="ancestral-tablet-gender-section ancestral-tablet-gender-section-option1">
                      <div className="ancestral-tablet-gender-male">
                        <div className="ancestral-tablet-text-container">
                          <h4 className="ancestral-tablet-gender-title">남성</h4>
                          <p className="ancestral-tablet-gender-description">남자의 지방에는 제사 대상의 관직명이 표시됩니다.< br /> 아래에서 선택한 광직명이 선택하신 기본 양식에 적용되어 출력됩니다.</p>
                        </div>
                        <div className="ancestral-tablet-male-options">
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="student-mixed"
                              checked={selectedMaleTitle === 'student'}
                              onChange={() => handleMaleTitleChange('student')}
                            />
                            <label htmlFor="student-mixed">학생 - 일반적으로 가장 널리 쓰임</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="chairman-mixed"
                              checked={selectedMaleTitle === 'chairman'}
                              onChange={() => handleMaleTitleChange('chairman')}
                            />
                            <label htmlFor="chairman-mixed">회장 - 법인/단체의 장(00협회장,00회장 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="military-officer-mixed"
                              checked={selectedMaleTitle === 'military'}
                              onChange={() => handleMaleTitleChange('military')}
                            />
                            <label htmlFor="military-officer-mixed">군관 - 군인의 경우 사용(장군, 고위장교 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="scholar-mixed"
                              checked={selectedMaleTitle === 'professional'}
                              onChange={() => handleMaleTitleChange('professional')}
                            />
                            <label htmlFor="scholar-mixed">처사 - 전문직 종사자(회계사, 변리사등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="judge-mixed"
                              checked={selectedMaleTitle === 'judge'}
                              onChange={() => handleMaleTitleChange('judge')}
                            />
                            <label htmlFor="judge-mixed">법관 - 법조인의 경우 사용(판, 검사, 변호사 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="executive-mixed"
                              checked={selectedMaleTitle === 'executive'}
                              onChange={() => handleMaleTitleChange('executive')}
                            />
                            <label htmlFor="executive-mixed">임원 - 대기업 고위직의 경우(대기업 회장, 사장, 간부 등)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="legislator-mixed"
                              checked={selectedMaleTitle === 'legislator'}
                              onChange={() => handleMaleTitleChange('legislator')}
                            />
                            <label htmlFor="legislator-mixed">의원 - 정치인(국회의원, 자치단체의원)</label>
                          </div>
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="male-title"
                              id="academic-mixed"
                              checked={selectedMaleTitle === 'scholar'}
                              onChange={() => handleMaleTitleChange('scholar')}
                            />
                            <label htmlFor="academic-mixed">학자 - 학계에 계셨던 분(교수, 박사, 학자 등)</label>
                          </div>
                        </div>
                      </div>

                      <div className="ancestral-tablet-gender-female">
                        <div className="ancestral-tablet-text-container">
                          <h4 className="ancestral-tablet-gender-title">여성</h4>
                          <p className="ancestral-tablet-gender-description">제사 대상이 여성일 경우 본관과 성씨가 입력됩니다. <br /> 본관 성씨를 빈칸에 한글로 적으시면 선택하신 기본양식에 적용되어 출력됩니다.</p>
                        </div>
                        <div className="ancestral-tablet-female-input">
                          <div className="ancestral-tablet-option">
                            <input
                              type="radio"
                              name="female-input"
                              id="bon-gwan-input-mixed"
                              checked={selectedFemaleInput === 'bon-gwan-input-mixed'}
                              onChange={() => handleFemaleInputChange('bon-gwan-input-mixed')}
                            />
                            <label htmlFor="bon-gwan-input-mixed">본관 성씨 입력
                            </label>
                          </div>
                          <div className="ancestral-tablet-input-group">
                            <input
                              type="text"
                              className="ancestral-tablet-text-input"
                              placeholder="예) 김해김, 경주김"
                              value={inputValue}
                              onChange={(e) => handleTextInputChange(e.target.value)}
                            />
                            <button className="ancestral-tablet-confirm-btn" onClick={() => handleLastNameCheck()}>확인</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Print Button */}
                <div className="ancestral-tablet-print-section">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <label htmlFor="orientation-select-pc" style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>용지 방향:</label>
                    <select 
                      id="orientation-select-pc"
                      value={orientation} 
                      onChange={(e) => setOrientation(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#374151',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        minWidth: '180px'
                      }}
                    >
                      <option value="landscape">가로 (Landscape)</option>
                      <option value="portrait">세로 (Portrait)</option>
                    </select>
                  </div>
                  <button className="ancestral-tablet-print-btn" onClick={handlePrintAncestralTablet}>지방 출력</button>
                </div>
              </div>

            </div>
          </div>
        </div>

      </main>

      {/* Print Preview Modal */}
      <PrintPreviewModal
        isOpen={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        printData={null}
      />
    </div>
  );
};

export default AncestralTabletPC; 